import {
  Badge,
  Button,
  Card,
  Container,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@components/shared/PageHeader';
import {
  BODY_IMAGE_EVENT_MAP,
  BODY_IMAGE_EVENT_NAMES,
  formatBodyImageEventLabel,
} from '../features/body-image/eventMap';
import {
  BODY_IMAGE_MANNEQUIN_EVENT_NAME,
  BODY_IMAGE_ROTATE_EVENT_NAME,
  buildBodyImageMannequinSelectPayload,
  buildBodyImageMannequinsComparePayload,
  buildBodyImageMannequinsHomePayload,
  buildBodyImageMannequinsRotatePayload,
  buildBodyImageStartPayload,
  getBodyImageSelectedFigure,
  isBodyImageAblyEventName,
  resolveBodyImageBiologicalSex,
} from '../features/body-image/utils';
import {
  fetchPatientById,
  fetchPatientMeasurementsHistory,
} from '../features/patients/services/patientsService';
import { useAbly } from '../providers/AblyProvider';
import {
  getConfiguration,
  getConfigurations,
  updateConfiguration,
} from '../services/configurationManager';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { formatTimestampToTime } from '../utils/formatDuration';
import { extractMessageName } from '../utils/sessionMessageHandlers';

type TrafficDirection = 'out' | 'in';

type LifecycleKind =
  | 'sessionStartedAt'
  | 'sessionEndedAt'
  | 'cancelSession'
  | 'sessionEnded';

type CapturedTraffic = {
  id: string;
  receivedAt: number;
  direction: TrafficDirection;
  event: string;
  detail: string;
  payload: Record<string, unknown> | null;
};

const OPEN_STATUSES = ['queued', 'initializing', 'in-progress'] as const;

const SAMPLE_COMPARISON_PARAMETERS = {
  weight: 58,
  waist: 70,
  hips: 96,
  bust: 92,
};

const FIGURE_OPTIONS = Array.from({ length: 9 }, (_, index) => {
  const value = String(index + 1);
  return { value, label: `Figura ${value}` };
});

const LIFECYCLE_NAMES = new Set([
  'sessionStartedAt',
  'sessionEndedAt',
  'cancelSession',
  'sessionEnded',
]);

function parseInboundData(data: unknown): Record<string, unknown> | null {
  let parsed: unknown = data;
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return parsed as Record<string, unknown>;
  }
  return null;
}

function trafficDetail(
  eventName: string,
  payload: Record<string, unknown> | null
): string {
  if (payload == null) return eventName;
  return formatBodyImageEventLabel(eventName, payload);
}

function lifecycleKind(messageName: string): LifecycleKind | null {
  if (messageName === 'sessionStartedAt') return 'sessionStartedAt';
  if (messageName === 'sessionEndedAt') return 'sessionEndedAt';
  if (messageName === 'cancelSession') return 'cancelSession';
  if (messageName === 'sessionEnded') return 'sessionEnded';
  return null;
}

function makeTrafficId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function AdminBodyImageEvents() {
  const { sendMessage, subscribeToMessages } = useAbly();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [traffic, setTraffic] = useState<CapturedTraffic[]>([]);
  const [listening, setListening] = useState(false);
  const [hubStartSent, setHubStartSent] = useState(false);
  const [figure, setFigure] = useState('3');
  const [rotateDegrees, setRotateDegrees] = useState(90);
  const [rotationAngle, setRotationAngle] = useState(0);

  const figureNumber = Number(figure) || 3;
  const absRotate = Math.max(1, Math.min(360, Math.abs(rotateDegrees) || 90));

  const listQueries = useQueries({
    queries: OPEN_STATUSES.map(status => ({
      queryKey: ['admin-body-image-events', 'list', status] as const,
      queryFn: () => getConfigurations({ status, page: 1, size: 100 }),
    })),
  });

  const sessions = useMemo(() => {
    const byId = new Map<number, DeviceConfigurationApi>();
    for (const q of listQueries) {
      for (const item of q.data?.items ?? []) {
        if (item.type === 'body_image') {
          byId.set(item.id, item);
        }
      }
    }
    return [...byId.values()].sort((a, b) => b.id - a.id);
  }, [listQueries]);

  const sessionQuery = useQuery({
    queryKey: ['admin-body-image-events', 'session', selectedId],
    queryFn: async () => {
      if (!selectedId) return null;
      const res = await getConfiguration(selectedId);
      return res.data;
    },
    enabled: Boolean(selectedId),
  });

  const session = sessionQuery.data ?? null;
  const selectedFigure = session
    ? getBodyImageSelectedFigure(session.config)
    : null;

  const publishedNames = useMemo(() => {
    const names = new Set<string>();
    for (const entry of traffic) {
      if (entry.direction === 'out') names.add(entry.event);
    }
    return names;
  }, [traffic]);

  const coverage = useMemo(() => {
    return BODY_IMAGE_EVENT_NAMES.map(name => {
      const published = publishedNames.has(name);
      return {
        name,
        label: BODY_IMAGE_EVENT_MAP[name].label,
        panel: BODY_IMAGE_EVENT_MAP[name].panel,
        status: published ? ('published' as const) : ('missing' as const),
      };
    });
  }, [publishedNames]);

  const lifecycleSummary = useMemo(() => {
    const inbound = traffic.filter(e => e.direction === 'in');
    const has = (name: string) => inbound.some(e => e.event === name);
    return {
      started: has('sessionStartedAt'),
      endedAt: has('sessionEndedAt'),
      cancelled: has('cancelSession'),
      sessionEnded: has('sessionEnded'),
    };
  }, [traffic]);

  useEffect(() => {
    setTraffic([]);
    setListening(false);
    setHubStartSent(false);
    setRotationAngle(0);
  }, [selectedId]);

  useEffect(() => {
    if (selectedFigure != null) {
      setFigure(String(selectedFigure));
    }
  }, [selectedFigure]);

  const appendOut = useCallback(
    (eventName: string, payload: Record<string, unknown>) => {
      setTraffic(prev => [
        ...prev,
        {
          id: makeTrafficId(),
          receivedAt: Date.now(),
          direction: 'out',
          event: eventName,
          detail: trafficDetail(eventName, payload),
          payload,
        },
      ]);
    },
    []
  );

  const publish = useCallback(
    (eventName: string, payload: Record<string, unknown>) => {
      if (!session) return;
      sendMessage(eventName, payload);
      appendOut(eventName, payload);
      notifications.show({
        color: 'green',
        title: 'Body Image → Ably',
        message: formatBodyImageEventLabel(eventName, payload),
      });
    },
    [appendOut, sendMessage, session]
  );

  useEffect(() => {
    if (!listening || !selectedId || !session) return;

    const unsubscribe = subscribeToMessages(
      message => {
        const messageName = extractMessageName(message);
        const payload = parseInboundData(message.data);

        if (isBodyImageAblyEventName(messageName)) {
          if (payload == null) return true;
          if (
            typeof payload.sessionId === 'string' &&
            payload.sessionId !== selectedId
          ) {
            return false;
          }
          return true;
        }

        const kind = lifecycleKind(messageName);
        if (!kind || payload == null) return false;

        if (kind === 'cancelSession') {
          return (
            typeof payload.code === 'string' &&
            payload.command === 'cancel' &&
            (!session.deviceCode || payload.code === session.deviceCode)
          );
        }

        if (kind === 'sessionEnded') {
          return (
            typeof payload.deviceCode === 'string' &&
            typeof payload.sessionId === 'string' &&
            payload.sessionId === selectedId
          );
        }

        if (
          typeof payload.sessionId === 'string' &&
          payload.sessionId !== selectedId
        ) {
          return false;
        }
        return true;
      },
      message => {
        const messageName = extractMessageName(message);
        const payload = parseInboundData(message.data);
        setTraffic(prev => [
          ...prev,
          {
            id: makeTrafficId(),
            receivedAt: Date.now(),
            direction: 'in',
            event: messageName,
            detail: LIFECYCLE_NAMES.has(messageName)
              ? messageName
              : trafficDetail(messageName, payload),
            payload,
          },
        ]);
      }
    );

    return unsubscribe;
  }, [listening, selectedId, session, subscribeToMessages]);

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!session || !selectedId) {
        throw new Error('Seleziona una sessione body image');
      }
      await updateConfiguration(session.id, {
        status: 'in-progress',
        started_at: new Date().toISOString(),
      });

      const patientId = session.patient_id;
      const [patient, measurements] = await Promise.all([
        patientId > 0 ? fetchPatientById(patientId) : Promise.resolve(null),
        patientId > 0
          ? fetchPatientMeasurementsHistory(patientId)
          : Promise.resolve([]),
      ]);
      const latestMeasurement = measurements[0] ?? null;
      const biologicalSex = resolveBodyImageBiologicalSex(
        patient?.profile?.biologicalSex ?? null,
        latestMeasurement
      );

      if (biologicalSex == null) {
        throw new Error(
          'Sesso biologico del paziente mancante (male/female). Completa il profilo paziente prima di avviare.'
        );
      }

      const startPayload = buildBodyImageStartPayload(
        session,
        latestMeasurement,
        biologicalSex
      );
      sendMessage('start_bodyimage', startPayload);
      appendOut('start_bodyimage', startPayload);

      const configuredFigure = getBodyImageSelectedFigure(session.config);
      if (configuredFigure != null) {
        const mannequinPayload = buildBodyImageMannequinSelectPayload(
          session,
          configuredFigure
        );
        sendMessage(BODY_IMAGE_MANNEQUIN_EVENT_NAME, mannequinPayload);
        appendOut(BODY_IMAGE_MANNEQUIN_EVENT_NAME, mannequinPayload);
      }
    },
    onMutate: () => {
      setTraffic([]);
    },
    onSuccess: () => {
      setListening(true);
      setHubStartSent(true);
      notifications.show({
        color: 'teal',
        title: 'Sessione avviata',
        message:
          'Pubblicato start_bodyimage. In ascolto di eventi Body Image e lifecycle.',
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Avvio fallito',
        message: error instanceof Error ? error.message : 'Impossibile avviare',
      });
    },
  });

  const selectData = sessions.map(item => ({
    value: String(item.id),
    label: `#${item.id} · ${item.deviceCode} · ${item.scenario} · ${item.status}`,
  }));

  const listLoading = listQueries.some(q => q.isLoading);
  const commandsDisabled = !session || !listening;

  const onSelect = useCallback((value: string | null) => {
    setSelectedId(value);
  }, []);

  const publishedCount = coverage.filter(c => c.status === 'published').length;
  const missingCount = coverage.filter(c => c.status === 'missing').length;

  return (
    <Container size='xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title='Body image'
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            {
              label: 'App Playground testing',
              href: '/admin/app-playground',
            },
            { label: 'Body image' },
          ]}
          showBackButton
          backUrl='/admin/app-playground'
        />
        <Text size='sm' c='dimmed'>
          Avvia una sessione body_image esistente con{' '}
          <Text span fw={600}>
            start_bodyimage
          </Text>{' '}
          (non startSession) e pubblica tutti gli eventi del catalogo
          hub→device. Il traffico IN cattura echo e lifecycle generici.
        </Text>

        <Card withBorder p='md'>
          <Stack gap='sm'>
            <Select
              label='Sessione body image (non completata)'
              placeholder={listLoading ? 'Caricamento…' : 'Scegli una sessione'}
              data={selectData}
              value={selectedId}
              onChange={onSelect}
              searchable
              nothingFoundMessage='Nessuna sessione body image aperta'
            />
            <Group>
              <Button
                onClick={() => startMutation.mutate()}
                loading={startMutation.isPending}
                disabled={!session}
              >
                Avvia
              </Button>
              {listening && (
                <Badge color='teal' variant='light'>
                  In ascolto
                </Badge>
              )}
              {hubStartSent && (
                <Badge color='blue' variant='light'>
                  start_bodyimage inviato
                </Badge>
              )}
            </Group>
          </Stack>
        </Card>

        {session && (
          <Card withBorder p='md'>
            <Stack gap='xs'>
              <Title order={5}>Sessione</Title>
              <Text size='xs' c='dimmed'>
                Device {session.deviceCode} · mappa {session.scenario} · status{' '}
                {session.status} · selectedFigure{' '}
                {selectedFigure != null ? selectedFigure : 'nessuna'}
              </Text>
              <Group grow align='flex-end'>
                <Select
                  label='Figura (1–9)'
                  data={FIGURE_OPTIONS}
                  value={figure}
                  onChange={value => {
                    if (value) setFigure(value);
                  }}
                />
                <NumberInput
                  label='Gradi rotazione (|n|)'
                  value={rotateDegrees}
                  onChange={value =>
                    setRotateDegrees(
                      typeof value === 'number' ? value : Number(value) || 90
                    )
                  }
                  min={1}
                  max={360}
                />
              </Group>
            </Stack>
          </Card>
        )}

        {session && listening && (
          <>
            <Card withBorder p='md'>
              <Stack gap='sm'>
                <Title order={5}>Nuovo</Title>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing='sm'>
                  <Button
                    variant='light'
                    disabled={commandsDisabled}
                    onClick={() =>
                      publish(
                        'bodyImage:mannequins:home',
                        buildBodyImageMannequinsHomePayload(session)
                      )
                    }
                  >
                    mannequins:home
                  </Button>
                  <Button
                    variant='light'
                    disabled={commandsDisabled}
                    onClick={() =>
                      publish(
                        BODY_IMAGE_MANNEQUIN_EVENT_NAME,
                        buildBodyImageMannequinSelectPayload(
                          session,
                          figureNumber
                        )
                      )
                    }
                  >
                    mannequin ({figureNumber})
                  </Button>
                  <Button
                    variant='light'
                    disabled={commandsDisabled}
                    onClick={() => {
                      const next = Math.max(-360, rotationAngle - absRotate);
                      setRotationAngle(next);
                      publish(
                        BODY_IMAGE_ROTATE_EVENT_NAME,
                        buildBodyImageMannequinsRotatePayload(session, next)
                      );
                    }}
                  >
                    rotate −{absRotate}°
                  </Button>
                  <Button
                    variant='light'
                    disabled={commandsDisabled}
                    onClick={() => {
                      const next = Math.min(360, rotationAngle + absRotate);
                      setRotationAngle(next);
                      publish(
                        BODY_IMAGE_ROTATE_EVENT_NAME,
                        buildBodyImageMannequinsRotatePayload(session, next)
                      );
                    }}
                  >
                    rotate +{absRotate}°
                  </Button>
                  <Button
                    variant='light'
                    disabled={commandsDisabled}
                    onClick={() => {
                      setRotationAngle(0);
                      publish(
                        BODY_IMAGE_ROTATE_EVENT_NAME,
                        buildBodyImageMannequinsRotatePayload(session, 0)
                      );
                    }}
                  >
                    rotate reset 0°
                  </Button>
                  <Button
                    variant='light'
                    disabled={commandsDisabled}
                    onClick={() =>
                      publish(
                        'bodyImage:mannequins:compare',
                        buildBodyImageMannequinsComparePayload(
                          session,
                          figureNumber,
                          {
                            id: -1,
                            label: 'Playground Variante A',
                            parameters: SAMPLE_COMPARISON_PARAMETERS,
                          }
                        )
                      )
                    }
                  >
                    mannequins:compare
                  </Button>
                </SimpleGrid>
                <Text size='xs' c='dimmed'>
                  Angolo accumulato Nuovo: {rotationAngle}°
                </Text>
              </Stack>
            </Card>
          </>
        )}

        <Card withBorder p='md'>
          <Stack gap='sm'>
            <Group justify='space-between'>
              <Title order={5}>Coverage catalogo</Title>
              <Text size='xs' c='dimmed'>
                {publishedCount} pubblicati · {missingCount} mancanti · angolo{' '}
                {rotationAngle}°
              </Text>
            </Group>
            <Text size='xs' c='dimmed'>
              Hub start: {hubStartSent ? 'inviato' : 'non ancora'} · lifecycle
              IN — start {lifecycleSummary.started ? 'sì' : 'no'}, endAt{' '}
              {lifecycleSummary.endedAt ? 'sì' : 'no'}, cancel{' '}
              {lifecycleSummary.cancelled ? 'sì' : 'no'}, sessionEnded{' '}
              {lifecycleSummary.sessionEnded ? 'sì' : 'no'}
            </Text>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Evento</Table.Th>
                  <Table.Th>Label</Table.Th>
                  <Table.Th>Pannello</Table.Th>
                  <Table.Th>Stato</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {coverage.map(row => (
                  <Table.Tr key={row.name}>
                    <Table.Td>
                      <Text size='sm' ff='monospace'>
                        {row.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>{row.label}</Table.Td>
                    <Table.Td>{row.panel}</Table.Td>
                    <Table.Td>
                      <Badge
                        color={row.status === 'published' ? 'teal' : 'orange'}
                        variant='light'
                      >
                        {row.status}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        </Card>

        <Card withBorder p='md'>
          <Stack gap='sm'>
            <Group justify='space-between'>
              <Title order={5}>Traffico Ably</Title>
              <Button
                size='xs'
                variant='subtle'
                disabled={traffic.length === 0}
                onClick={() => setTraffic([])}
              >
                Pulisci
              </Button>
            </Group>
            {traffic.length === 0 ? (
              <Text size='sm' c='dimmed'>
                Nessun evento ancora. Avvia la sessione e usa i comandi.
              </Text>
            ) : (
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Ora</Table.Th>
                    <Table.Th>Dir</Table.Th>
                    <Table.Th>Evento</Table.Th>
                    <Table.Th>Dettaglio</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {[...traffic].reverse().map(entry => (
                    <Table.Tr key={entry.id}>
                      <Table.Td>
                        {formatTimestampToTime(entry.receivedAt)}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={entry.direction === 'out' ? 'blue' : 'grape'}
                          variant='light'
                        >
                          {entry.direction.toUpperCase()}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm' ff='monospace'>
                          {entry.event}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>{entry.detail}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
